import Big from 'big.js';
import { DateTime } from 'luxon';
import axios from 'smg/axios';
import Batch from 'smg/batch';
import * as buttons from 'smg/buttons';
import * as hyejoo from 'smg/customer-api/hyejoo';
import * as lineitemsapi from 'smg/customer-api/lineitems';
import * as session from 'smg/customer-api/session';
import Modal from 'smg/modal';
import * as models from 'smg/models';
import { G } from 'smg/querystring';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import { DollarFormat } from 'smg/util';
import * as carts from '../carts';
import * as payments from '../payments';
declare interface orderResp {
    order: models.Order;
}

export default async function main() {
    const sess = session.Session().catch(() => undefined);

    const spinner = document.querySelector('#spinner');
    const orderFoundContainer = document.querySelector('#order-found-container') as HTMLElement;
    const notFoundContainer = document.querySelector('#order-not-found-container') as HTMLElement;
    const viewStagesBtn = document.querySelector('#view-stages-btn') as HTMLButtonElement;
    const invoiceNumber = document.querySelector('#invoice-number') as HTMLElement;
    const orderCreatedDate = document.querySelector('#order-created-date') as HTMLElement;
    const proofsContainer = document.querySelector('#proofs-container') as HTMLElement;

    const id = G().Get('id');

    if (!id) {
        showNotFound();
        return;
    }

    const b = new Batch();

    const carriers = b.get<hyejoo.Carriers>('/carriers', {
        params: {
            'data': 'name,link',
        },
    }).then(resp => {
        const m = new Map<number, models.Carrier>();
        for (const carrier of resp.data.carriers) {
            m.set(carrier.id || 0, carrier);
        }
        return m;
    });

    void b.get<hyejoo.LineItems>('/v2/line-items', {
        params: {
            'data': 'name,pluralName,product',
        },
    }).then(resp => {
        const m = new Map<string, models.LineItem>();
        for (const lineItem of resp.data.lineItems) {
            if (lineItem.id) {
                m.set(lineItem.id, lineItem);
            }
        }
        return m;
    });

    void b.Exec();

    const checkMainStageAndCarrier = async (order: models.Order) => {
        let orderStatus: string;
        let hasShipments = false;
        let delivered = false;
        let partiallyDelivered = false;
        if (order.orderShippingOptions?.length) {
            for (const orderShippingOption of order.orderShippingOptions) {
                if (orderShippingOption.orderShippingOptionShipments?.length) {
                    for (const orderShippingOptionShipment of orderShippingOption.orderShippingOptionShipments) {
                        const shipment = orderShippingOptionShipment.shipment;

                        if (!shipment) {
                            hasShipments = false;
                            continue;
                        } else {
                            hasShipments = true;
                        }

                        if (shipment.delivered) {
                            delivered = true;
                        } else {
                            partiallyDelivered = true;
                        }

                        const carrier = (await carriers).get(shipment.carrierID as models.CarrierID);

                        const trackingNumbersContainer = document.querySelector('#tracking-numbers-container') as HTMLElement;

                        if (carrier) {
                            const a = document.createElement('a') as HTMLAnchorElement;
                            const carrierId = (await carriers).get(shipment.carrierID || 0);
                            if (carrierId?.link) {
                                a.href = carrierId.link.replace('%%', shipment.trackingNumber || '');
                            }
                            a.textContent = `${shipment.trackingNumber || ''} (${shipment.carrier?.name || ''})`;
                            a.classList.add('text-secondary');

                            trackingNumbersContainer.appendChild(a);
                        } else {
                            trackingNumbersContainer.appendChild(document.createTextNode(shipment.trackingNumber || ''));
                        }

                        if (hasShipments) {
                            trackingNumbersContainer.appendChild(document.createTextNode(', '));
                        }
                    }
                } else {
                    const trackingNumbersLabel = document.querySelector('#tracking-numbers-label') as HTMLElement;
                    trackingNumbersLabel.classList.add('d-none');
                }
            }
        }

        if (delivered && partiallyDelivered) {
            partiallyDelivered = true;
        } else {
            partiallyDelivered = false;
        }

        switch (true) {
        case delivered && !partiallyDelivered:
            orderStatus = 'Delivered';
            return orderStatus;
        case partiallyDelivered:
            orderStatus = 'Partially Delivered';
            return orderStatus;
        case hasShipments:
            orderStatus = 'Shipped';
            return orderStatus;
        case order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_CONFIRMED:
            orderStatus = 'In Production';
            return orderStatus;
        case order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_UNCONFIRMED:
            orderStatus = 'Preparing for Production';
            return orderStatus;
        case order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_ORDER:
            orderStatus = 'Order Received';
            return orderStatus;
        }
    };

    const renderTrackingBar = (orderStatus: string) => {
        const orderReceivedIcon = document.querySelector('#order-received-icon') as HTMLElement;
        const orderReceivedIconDiv = document.querySelector('#order-received-icon-div') as HTMLElement;
        const orderReceivedLabel = document.querySelector('#order-received-label') as HTMLElement;
        const preparingForProductionIcon = document.querySelector('#preparing-for-production-icon') as HTMLElement;
        const preparingForProductionIconDiv = document.querySelector('#preparing-for-production-icon-div') as HTMLElement;
        const preparingForProductionLabel = document.querySelector('#preparing-for-production-label') as HTMLElement;
        const inProductionIcon = document.querySelector('#in-production-icon') as HTMLElement;
        const inProductionIconDiv = document.querySelector('#in-production-icon-div') as HTMLElement;
        const inProductionLabel = document.querySelector('#in-production-label') as HTMLElement;
        const shippedIcon = document.querySelector('#shipped-icon') as HTMLElement;
        const shippedIconDiv = document.querySelector('#shipped-icon-div') as HTMLElement;
        const shippedLabel = document.querySelector('#shipped-label') as HTMLElement;
        const deliveredIcon = document.querySelector('#delivered-icon') as HTMLElement;
        const deliveredLabel = document.querySelector('#delivered-label') as HTMLElement;

        switch (orderStatus) {
        case 'Delivered':
            orderReceivedIcon.classList.remove('text-muted');
            orderReceivedIcon.classList.add('text-primary');
            orderReceivedIconDiv.firstElementChild?.classList.add('progress-bar-fill');
            orderReceivedLabel.classList.remove('text-muted');
            orderReceivedLabel.classList.add('fst-italic');
            setTimeout(() => {
                preparingForProductionIcon.classList.remove('text-muted');
                preparingForProductionIcon.classList.add('text-primary');
            }, 1100);
            preparingForProductionLabel.classList.remove('text-muted');
            preparingForProductionLabel.classList.add('fst-italic');
            setTimeout(() => {
                preparingForProductionIconDiv.firstElementChild?.classList.add('progress-bar-fill');
            }, 1150);
            setTimeout(() => {
                inProductionIcon.classList.remove('text-muted');
                inProductionIcon.classList.add('text-primary');
            }, 2200);
            inProductionLabel.classList.remove('text-muted');
            inProductionLabel.classList.add('fst-italic');
            setTimeout(() => {
                inProductionIconDiv.firstElementChild?.classList.add('progress-bar-fill');
            }, 2250);
            setTimeout(() => {
                shippedIcon.classList.remove('text-muted');
                shippedIcon.classList.add('text-primary');
            }, 3300);
            shippedLabel.classList.remove('text-muted');
            shippedLabel.classList.add('fst-italic');
            setTimeout(() => {
                shippedIconDiv.firstElementChild?.classList.add('progress-bar-fill');
            }, 3350);
            setTimeout(() => {
                deliveredIcon.classList.remove('text-muted');
                deliveredIcon.classList.add('text-primary');
            }, 4400);
            deliveredLabel.classList.remove('text-muted');
            deliveredLabel.classList.add('fst-italic');
            break;

        case 'Partially Delivered':
            orderReceivedIcon.classList.remove('text-muted');
            orderReceivedIcon.classList.add('text-primary');
            orderReceivedIconDiv.firstElementChild?.classList.add('progress-bar-fill');
            orderReceivedLabel.classList.remove('text-muted');
            orderReceivedLabel.classList.add('fst-italic');
            setTimeout(() => {
                preparingForProductionIcon.classList.remove('text-muted');
                preparingForProductionIcon.classList.add('text-primary');
            }, 1100);
            preparingForProductionLabel.classList.remove('text-muted');
            preparingForProductionLabel.classList.add('fst-italic');
            setTimeout(() => {
                preparingForProductionIconDiv.firstElementChild?.classList.add('progress-bar-fill');
            }, 1150);
            setTimeout(() => {
                inProductionIcon.classList.remove('text-muted');
                inProductionIcon.classList.add('text-primary');
            }, 2200);
            inProductionLabel.classList.remove('text-muted');
            inProductionLabel.classList.add('fst-italic');
            setTimeout(() => {
                inProductionIconDiv.firstElementChild?.classList.add('progress-bar-fill');
            }, 2250);
            setTimeout(() => {
                shippedIcon.classList.remove('text-muted');
                shippedIcon.classList.add('text-primary');
            }, 3300);
            shippedLabel.classList.remove('text-muted');
            shippedLabel.classList.add('fst-italic');
            setTimeout(() => {
                shippedIconDiv.firstElementChild?.classList.add('progress-bar-fill-half');
            }, 3350);
            break;

        case 'Shipped':
            orderReceivedIcon.classList.remove('text-muted');
            orderReceivedIcon.classList.add('text-primary');
            orderReceivedIconDiv.firstElementChild?.classList.add('progress-bar-fill');
            orderReceivedLabel.classList.remove('text-muted');
            orderReceivedLabel.classList.add('fst-italic');
            setTimeout(() => {
                preparingForProductionIcon.classList.remove('text-muted');
                preparingForProductionIcon.classList.add('text-primary');
            }, 1100);
            preparingForProductionLabel.classList.remove('text-muted');
            preparingForProductionLabel.classList.add('fst-italic');
            setTimeout(() => {
                preparingForProductionIconDiv.firstElementChild?.classList.add('progress-bar-fill');
            }, 1150);
            setTimeout(() => {
                inProductionIcon.classList.remove('text-muted');
                inProductionIcon.classList.add('text-primary');
            }, 2200);
            inProductionLabel.classList.remove('text-muted');
            inProductionLabel.classList.add('fst-italic');
            setTimeout(() => {
                inProductionIconDiv.firstElementChild?.classList.add('progress-bar-fill');
            }, 2250);
            setTimeout(() => {
                shippedIcon.classList.remove('text-muted');
                shippedIcon.classList.add('text-primary');
            }, 3300);
            shippedLabel.classList.remove('text-muted');
            shippedLabel.classList.add('fst-italic');
            break;

        case 'In Production':
            orderReceivedIcon.classList.remove('text-muted');
            orderReceivedIcon.classList.add('text-primary');
            orderReceivedLabel.classList.remove('text-muted');
            orderReceivedIconDiv.firstElementChild?.classList.add('progress-bar-fill');
            orderReceivedLabel.classList.add('fst-italic');
            setTimeout(() => {
                preparingForProductionIcon.classList.remove('text-muted');
                preparingForProductionIcon.classList.add('text-primary');
            }, 1100);
            preparingForProductionLabel.classList.remove('text-muted');
            preparingForProductionLabel.classList.add('fst-italic');
            setTimeout(() => {
                preparingForProductionIconDiv.firstElementChild?.classList.add('progress-bar-fill');
            }, 1150);
            setTimeout(() => {
                inProductionIcon.classList.remove('text-muted');
                inProductionIcon.classList.add('text-primary');
            }, 2200);
            inProductionLabel.classList.remove('text-muted');
            inProductionLabel.classList.add('fst-italic');
            setTimeout(() => {
                inProductionIconDiv.firstElementChild?.classList.add('progress-bar-fill-half');
            }, 2250);
            break;

        case 'Preparing for Production':
            orderReceivedIcon.classList.remove('text-muted');
            orderReceivedIcon.classList.add('text-primary');
            orderReceivedIconDiv.firstElementChild?.classList.add('progress-bar-fill');
            orderReceivedLabel.classList.remove('text-muted');
            orderReceivedLabel.classList.add('fst-italic');
            setTimeout(() => {
                preparingForProductionIcon.classList.remove('text-muted');
                preparingForProductionIcon.classList.add('text-primary');
            }, 1100);
            preparingForProductionLabel.classList.remove('text-muted');
            preparingForProductionLabel.classList.add('fst-italic');
            setTimeout(() => {
                preparingForProductionIconDiv.firstElementChild?.classList.add('progress-bar-fill-half');
            }, 1150);
            break;

        case 'Order Received':
            orderReceivedIcon.classList.remove('text-muted');
            orderReceivedIcon.classList.add('text-primary');
            orderReceivedLabel.classList.remove('text-muted');
            orderReceivedLabel.classList.add('fst-italic');
            orderReceivedIconDiv.firstElementChild?.classList.add('progress-bar-fill-half');
            break;
        }

        const orderStatusLabel = document.querySelector('#order-status') as HTMLElement;
        orderStatusLabel.textContent = orderStatus;
    };

    const renderOrderProofs = (order: models.Order) => {
        if (order.orderProofs) {
            for (const proof of order.orderProofs) {
                const proofTemplate = template.Clone('#proofs');
                const proofThumbnail = proofTemplate.querySelector('#thumbnail') as HTMLImageElement;
                const proofLink = proofTemplate.querySelector('#proof-link') as HTMLAnchorElement;
                proofLink.href = proof.proof?.main || '';
                proofLink.dataset.caption = proof.proof?.name || '';
                proofThumbnail.src = proof.proof?.thumbnail || '';
                proofsContainer.append(proofTemplate);
            }
        }
    };

    const getLineItemInfo = async (order: models.Order) => {
        if (order.orderLineItems?.length) {
            for (const lineItem of order.orderLineItems) {
                if (!lineItem.lineItemModel?.shipping && !lineItem.lineItemModel?.tax) {
                    const lineItemListContainer = document.querySelector('#items-container') as HTMLElement;
                    const itemEl = document.createElement('li');
                    itemEl.classList.add('mb-2');
                    itemEl.textContent = [
                        (await lineitemsapi.LineItemMap()).get(lineItem.lineItemID || '')?.pluralName || lineItem.lineItem,
                        (await lineitemsapi.TypeMap()).get(lineItem.lineItemTypeID || '')?.name || lineItem.type,
                        (await lineitemsapi.SizeMap()).get(lineItem.lineItemTypeSizeID || '')?.name || lineItem.size,
                        lineItem.description,
                    ].filter(Boolean).join(' - ');
                    if ((lineItem.lineItemModel?.product || !lineItem.lineItemModel) && lineItem.lineItemID) {
                        const itemQty = document.createElement('span');
                        itemQty.classList.add('fw-bold', 'text-secondary', 'ps-2');
                        itemQty.textContent = ` x${String(lineItem.quantity)}`;
                        itemEl.appendChild(itemQty);
                    }
                    lineItemListContainer.append(itemEl);

                    const productionStageLabel = document.querySelector('#production-stage-label') as HTMLElement;
                    if (lineItem.orderLineItemStages?.length) {
                        productionStageLabel.classList.remove('d-none');
                        for (let i = 0; i < lineItem.orderLineItemStages.length; i++) {
                            if (i === lineItem.orderLineItemStages.length - 1) {
                                const productionStage = document.querySelector('#production-stage') as HTMLElement;
                                productionStage.textContent = lineItem.orderLineItemStages[i].stage?.name || '';
                            }
                        }
                    } else {
                        productionStageLabel.classList.add('d-none');
                    }
                }
            }
        }
    };

    const calculateOrderTotals = (order: models.Order) => {
        const balanceContainer = document.querySelector('#remaining-balance') as HTMLElement;
        const payBalanceBtn = document.querySelector('#pay-balance-btn') as HTMLElement;
        const balanceHr = document.querySelector('#balance-hr') as HTMLElement;
        const balance = document.querySelector('#balance') as HTMLElement;

        const subtotal = document.querySelector('#subtotal') as HTMLElement;
        const shippingTotal = document.querySelector('#shipping') as HTMLElement;
        const taxTotal = document.querySelector('#tax') as HTMLElement;
        const discounts = document.querySelector('#discounts') as HTMLElement;
        const total = document.querySelector('#total') as HTMLElement;

        const nums = {
            subtotal: Big('0'),
            discounts: Big('0'),
            shipping: Big('0'),
            tax: Big('0'),
            total: Big('0'),
        };

        if (Big(order.balance || '0').gte('0.05')) {
            balanceContainer.classList.remove('d-none');
            balance.textContent = DollarFormat(order.balance || Big('0'));
            payBalanceBtn.classList.remove('d-none');
            balanceHr.classList.remove('d-none');

            payBalanceBtn.addEventListener('click', async () => {
                await payments.PromptPayment(await sess, [order.id || ''], Big(order.balance || '0'), false).
                    then(() => {
                        window.location.reload();
                        toast.Success('Thank you for your payment');
                    });
            });
        }

        if (order.orderLineItems?.length) {
            for (const lineItem of order.orderLineItems) {
                if (Big(lineItem.price || 0).lt(0)) {
                    nums.discounts = nums.discounts.add(lineItem.price || 0);
                }

                const subtotal = Big(lineItem.price || 0).mul(lineItem.quantity || 0);
                nums.total = nums.total.add(subtotal);

                const childLineItems = (lineItem as models.OrderLineItem).orderChildLineItems || [];
                for (const childLineItem of childLineItems) {
                    const subtotal = Big(childLineItem.price || 0).mul(childLineItem.quantity || 0);
                    nums.total = nums.total.add(subtotal);

                    if (Big(childLineItem.price || 0).lt(0)) {
                        nums.discounts = nums.discounts.add(childLineItem.price || 0);
                    }
                }

                if (lineItem.lineItemModel?.shipping) {
                    nums.shipping = nums.shipping.add(Big(lineItem.price || 0) || 0);
                }

                if (lineItem.lineItemModel?.tax) {
                    nums.tax = nums.tax.add(Big(lineItem.price || 0) || 0);
                }
            }
        }

        subtotal.textContent = DollarFormat(nums.total.sub(nums.tax).sub(nums.shipping).sub(nums.discounts));
        discounts.textContent = DollarFormat(nums.discounts);
        shippingTotal.textContent = DollarFormat(nums.shipping);
        taxTotal.textContent = DollarFormat(nums.tax);
        total.textContent = DollarFormat(order.total || '0');
    };

    function showNotFound() {
        orderFoundContainer.classList.add('d-none');
        notFoundContainer.classList.remove('d-none');

        const spinner = document.querySelector('#spinner');
        spinner?.classList.add('d-none');
    }

    await axios.get<orderResp>(`/orders/${id}`).then(async resp => {
        const order = resp.data.order;

        invoiceNumber.textContent = `#${String(order.invoiceNumber) || ''}`;
        orderCreatedDate.textContent = DateTime.fromISO(order.created || '').toLocaleString(DateTime.DATETIME_FULL);

        await getLineItemInfo(order);
        calculateOrderTotals(order);
        renderOrderProofs(order);
        const orderStatus = await checkMainStageAndCarrier(order);
        renderTrackingBar(orderStatus as string);

        viewStagesBtn.addEventListener('click', () => {
            new Modal({ body: template.Clone('#coin-stages-template') });
        });

        const reorder = document.querySelector('#order-reorder') as HTMLButtonElement;
        reorder.addEventListener('click', () => {
            buttons.Start(reorder);
            void carts.AddCartItem({ orderID: id }).then(() => {
                reorder.classList.remove('btn-primary', 'btn-success', 'text-white');
                reorder.classList.add('btn-outline-success');

                reorder.querySelector('i')?.classList.remove('fa-shopping-cart');
                reorder.querySelector('i')?.classList.add('fa-check');
            }).finally(() => buttons.Stop(reorder));
        });

        const share = document.querySelector('#order-share') as HTMLAnchorElement;
        const shareHrefSubject = 'Signature Order Details';
        const shareHrefBody = `Here's a link to your order details:\n\n${window.location.href}`;
        share.href = `mailto:?subject=${encodeURIComponent(shareHrefSubject)}&body=${encodeURIComponent(shareHrefBody)}`;

        spinner?.classList.add('d-none');
        orderFoundContainer?.classList.remove('d-none');
    }).catch(err => {
        showNotFound();
        console.error(err);
    });
}